// PATH: src/pages/TrialsPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, Divider, Skeleton, Stack, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrowRounded";
import RestartAltIcon from "@mui/icons-material/RestartAltRounded";
import { useAppConfigsApi, useCurriculumApi, useTrialsApi } from "../api";
import type { CurriculumProgram, Trial, TrialEstimate, TrialResolved, UUID } from "../models/domain";
import { PageHeader, errorMessage, useConfirm, useToast } from "../components/ui";
import TrialDocumentPicker from "../components/trials/TrialDocumentPicker";
import TrialSettingsForm from "../components/trials/TrialSettingsForm";
import TrialEstimateView from "../components/trials/TrialEstimateView";
import TrialList from "../components/trials/TrialList";
import { embedRolePrompts } from "../components/settings/rolePrompts";
import { useEmbedPrompts } from "../components/settings/useEmbedPrompts";
import {
  EMPTY_TRIAL_FORM,
  TRIAL_POLL_MS,
  anyRunning,
  buildOverride,
  formFromResolved,
  formatUsd,
  overrideIsEmpty,
  selectionError,
  subjectOptions,
  type SubjectOption,
  type TrialForm,
} from "../components/trials/trialLogic";
import { declaredModelNames } from "../components/settings/llmCatalog";

/** Recalcul de l'estimation après la dernière frappe dans une consigne. */
const ESTIMATE_DEBOUNCE_MS = 600;

const Section: React.FC<{ step: number; title: string; hint?: string; children: React.ReactNode }> = ({
  step,
  title,
  hint,
  children,
}) => (
  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
    <Typography variant="h5">
      {step}. {title}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.5 }}>
        {hint}
      </Typography>
    )}
    <Box sx={{ mt: hint ? 0 : 1.5 }}>{children}</Box>
  </Box>
);

/**
 * Essais d'extraction : « ces réglages donnent-ils de meilleurs faits ? ».
 * On choisit quelques documents d'une matière, on règle l'extraction et la
 * relecture pour l'essai seulement, on voit le coût estimé, on lance ; puis on
 * compare, objectif par objectif, les faits de l'essai aux faits servis, avant
 * d'appliquer ou de jeter. Rien ne change pour les élèves avant « Appliquer ».
 */
const TrialsPage: React.FC = () => {
  const { getCurriculumOverview } = useCurriculumApi();
  const { getLlmCatalog } = useAppConfigsApi();
  const { estimateTrial, createTrial, listTrials } = useTrialsApi();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  /* ---------- Référentiel et modèles ---------- */
  const [programs, setPrograms] = React.useState<CurriculumProgram[] | null>(null);
  const [models, setModels] = React.useState<string[]>([]);
  const [catalogError, setCatalogError] = React.useState<string | null>(null);

  const loadCatalog = React.useCallback(async () => {
    setCatalogError(null);
    try {
      const [{ data }, catalog] = await Promise.all([
        getCurriculumOverview(),
        // Sans catalogue, la saisie reste libre : l'estimation refusera un modèle non déclaré ou non tarifé.
        getLlmCatalog().catch(() => null),
      ]);
      setPrograms(data.programs ?? []);
      // Le fournisseur de l'essai est celui de la matière : on propose les
      // modèles déclarés chez les fournisseurs actifs.
      setModels(catalog ? declaredModelNames({ providers: catalog.data.providers, models: catalog.data.models }) : []);
    } catch (e) {
      setCatalogError(errorMessage(e, "Impossible de charger le référentiel."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const subjectNames = React.useMemo(
    () => new Map((programs ? subjectOptions(programs) : []).map((s) => [s.id, s.name])),
    [programs],
  );

  /* ---------- Essais ---------- */
  const [trials, setTrials] = React.useState<Trial[] | null>(null);
  const [trialsError, setTrialsError] = React.useState<string | null>(null);

  const loadTrials = React.useCallback(async () => {
    try {
      const { data } = await listTrials();
      setTrials(data.trials ?? []);
      setTrialsError(null);
    } catch (e) {
      setTrialsError(errorMessage(e, "Impossible de charger les essais."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    void loadTrials();
  }, [loadTrials]);

  const running = trials ? anyRunning(trials) : false;
  React.useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => void loadTrials(), TRIAL_POLL_MS);
    return () => window.clearInterval(id);
  }, [running, loadTrials]);

  const basePrompts = useEmbedPrompts();
  const prompts = React.useMemo(
    () => ({
      fact_extraction: embedRolePrompts("fact_extraction", basePrompts.prompts, basePrompts.error),
      fact_review: embedRolePrompts("fact_review", basePrompts.prompts, basePrompts.error),
    }),
    [basePrompts.prompts, basePrompts.error],
  );

  /* ---------- Nouvel essai ---------- */
  const [subject, setSubject] = React.useState<SubjectOption | null>(null);
  const [selected, setSelected] = React.useState<UUID[]>([]);
  // Valeurs effectives de la matière : la référence de la surcharge.
  const [base, setBase] = React.useState<TrialResolved | null>(null);
  const [form, setForm] = React.useState<TrialForm>(EMPTY_TRIAL_FORM);
  const [estimate, setEstimate] = React.useState<{ key: string; data: TrialEstimate } | null>(null);
  const [estimating, setEstimating] = React.useState(false);
  const [estimateError, setEstimateError] = React.useState<string | null>(null);
  const [launching, setLaunching] = React.useState(false);
  const seq = React.useRef(0);

  const changeSubject = (s: SubjectOption | null) => {
    seq.current += 1;
    setSubject(s);
    setSelected([]);
    setBase(null);
    setForm(EMPTY_TRIAL_FORM);
    setEstimate(null);
    setEstimateError(null);
  };

  const override = React.useMemo(() => (base ? buildOverride(form, base) : {}), [form, base]);
  const selectedKey = selected.join(",");
  const key = `${selectedKey}|${JSON.stringify(override)}`;

  // Estimation : la première (surcharge vide) préremplit le formulaire ; les
  // suivantes suivent chaque modification, débouncées. Une réponse arrivée
  // après une modification plus récente est ignorée.
  React.useEffect(() => {
    // Retour à un état déjà estimé (ou sélection invalide) : la requête en
    // vol, s'il y en a une, ne vaut plus rien.
    if (!selected.length || selectionError(selected.length) || estimate?.key === key) {
      seq.current += 1;
      setEstimating(false);
      return;
    }
    const mine = ++seq.current;
    setEstimating(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await estimateTrial({ document_ids: selected, override });
        if (mine !== seq.current) return;
        if (!base) {
          setBase(data.resolved);
          setForm(formFromResolved(data.resolved));
        }
        setEstimate({ key, data });
        setEstimateError(null);
      } catch (e) {
        if (mine === seq.current) setEstimateError(errorMessage(e, "Estimation impossible."));
      } finally {
        if (mine === seq.current) setEstimating(false);
      }
    }, base ? ESTIMATE_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const fresh = Boolean(estimate && estimate.key === key && !estimating);
  const pickError = selected.length ? selectionError(selected.length) : null;

  const launch = async () => {
    if (!estimate || !fresh) return;
    const e = estimate.data;
    const ok = await confirm({
      title: "Lancer l'essai ?",
      message:
        `${e.objectives} objectif${e.objectives > 1 ? "s" : ""} dans ${e.documents.length} document${e.documents.length > 1 ? "s" : ""}, ` +
        (e.estimate_usd === null
          ? "coût estimé inconnu (une mesure ou un tarif manque)."
          : `coût estimé ${formatUsd(e.estimate_usd)}.`) +
        (overrideIsEmpty(override) ? "\nAucun réglage changé : l'essai rejoue les réglages de la matière." : "") +
        "\n\nRien ne change pour les élèves avant « Appliquer ».",
      confirmLabel: "Lancer",
    });
    if (!ok) return;
    setLaunching(true);
    try {
      const { data } = await createTrial({ document_ids: selected, override });
      toast.success("Essai lancé.");
      navigate(`/trials/${data.trial.id}`);
    } catch (err) {
      toast.error(errorMessage(err, "Impossible de lancer l'essai."));
      void loadTrials();
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <PageHeader
        title="Essais d'extraction"
        subtitle="Essayer d'autres réglages d'extraction et de relecture des faits sur quelques documents, comparer aux faits servis, puis appliquer ou jeter. Rien ne change pour les élèves avant « Appliquer »."
      />

      {catalogError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void loadCatalog()}>
              Réessayer
            </Button>
          }
        >
          {catalogError}
        </Alert>
      )}

      <Card>
        <Section step={1} title="Documents" hint="Une matière, puis 1 à 10 de ses documents.">
          {programs ? (
            <TrialDocumentPicker
              programs={programs}
              subject={subject}
              onSubject={changeSubject}
              selected={selected}
              onSelected={setSelected}
              disabled={launching}
            />
          ) : (
            !catalogError && <Skeleton height={40} sx={{ maxWidth: 420 }} />
          )}
          {pickError && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {pickError}
            </Alert>
          )}
        </Section>

        <Divider />
        <Section
          step={2}
          title="Réglages de l'essai"
          hint="Préremplis avec les réglages de la matière. Pour chaque agent, dans l'ordre où le modèle les reçoit : le prompt de base, la consigne héritée, puis la consigne de l'essai qui la remplace si vous la remplissez. Seuls les champs changés valent pour l'essai, et pour lui seul."
        >
          {base ? (
            <Stack spacing={1.5}>
              <TrialSettingsForm
                form={form}
                onChange={setForm}
                base={base}
                override={override}
                models={models}
                prompts={prompts}
                disabled={launching}
              />
              <Box>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  disabled={overrideIsEmpty(override) || launching}
                  onClick={() => setForm(formFromResolved(base))}
                >
                  Revenir aux réglages de la matière
                </Button>
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {!selected.length
                ? "Choisissez d'abord des documents."
                : estimateError
                  ? "Réglages de la matière illisibles : voir l'erreur de l'estimation ci-dessous."
                  : "Lecture des réglages de la matière…"}
            </Typography>
          )}
        </Section>

        <Divider />
        <Section step={3} title="Coût estimé">
          {selected.length ? (
            <TrialEstimateView estimate={estimate?.data ?? null} loading={estimating} error={estimateError} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              L'estimation apparaît dès qu'un document est choisi.
            </Typography>
          )}
        </Section>

        <Divider />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="flex-end"
          sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}
        >
          {running && (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Un essai tourne déjà : un seul à la fois.
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!fresh || Boolean(pickError) || running || launching || estimate?.data.objectives === 0}
            onClick={() => void launch()}
          >
            {launching ? "Lancement…" : "Lancer l'essai"}
          </Button>
        </Stack>
      </Card>

      <Card sx={{ mt: 2 }}>
        <Box sx={{ p: { xs: 2, md: 2.5 }, pb: 1 }}>
          <Typography variant="h5">Essais</Typography>
          <Typography variant="body2" color="text.secondary">
            Les 50 derniers. Ouvrez un essai pour comparer ses faits aux faits servis.
          </Typography>
        </Box>
        {trialsError && (
          <Alert
            severity="error"
            sx={{ mx: 2, mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void loadTrials()}>
                Réessayer
              </Button>
            }
          >
            {trialsError}
          </Alert>
        )}
        {trials ? (
          <TrialList trials={trials} subjectNames={subjectNames} onOpen={(t) => navigate(`/trials/${t.id}`)} />
        ) : (
          !trialsError && (
            <Box sx={{ px: 2.5, pb: 2 }}>
              <Skeleton height={36} />
              <Skeleton height={36} />
            </Box>
          )
        )}
      </Card>
    </Box>
  );
};

export default TrialsPage;
